// BCP規程生成API
import { apiClient } from './api';

export interface RegulationGenerationRequest {
  company_name: string;
  business_type: string;
  location: string;
  main_business?: string;
  employee_count?: number;
  annual_revenue?: string;
  // 復旧方針から引き継ぐデータ
  restore_policy_data?: Record<string, any>;
  important_business?: Array<{ name: string; reason: string }>;
  recovery_priority?: Array<{ rank: string; business: string; rto?: string }>;
  rto_settings?: Array<Record<string, string>>;
  risk_assessment?: Record<string, any>;
  // 生成オプション
  tenant_id?: string;
  search_query?: string;
  max_search_results?: number;
  use_parallel_generation?: boolean;
  use_section_based_generation?: boolean;
  extracted_info?: Record<string, any>;
}

export interface RegulationResponse {
  regulation_document: string;
  generation_metadata: {
    model: string;
    generation_method: string;
    total_generation_time_seconds: number;
    usage: Record<string, number>;
    phase_metadata?: any[];
  };
  company_info: Record<string, any>;
  restore_policy_info?: Record<string, any>;
  company_results_used: any[];
  template_results_used: any[];
}

// 非同期ジョブ関連の型定義
export type RegulationJobStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface RegulationJobResponse {
  job_id: string;
  status: RegulationJobStatus;
  result?: RegulationResponse;
  error?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

export const generateRegulation = async (
  request: RegulationGenerationRequest
): Promise<RegulationResponse> => {
  console.log('generateRegulation API call with request:', request);
  try {
    const response = await apiClient.postRaw<RegulationResponse>(
      '/api/regulation/generate',
      request,
      {
        timeout: 300000, // 5分のタイムアウト
      }
    );

    console.log('generateRegulation API response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('BCP規程生成エラー:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error response detail:', error.response?.data?.detail);
    throw new Error(
      error.response?.data?.detail ||
      'BCP規程の生成中にエラーが発生しました'
    );
  }
};

// 生成されたBCP規程をパース
export const parseRegulation = (document: string) => {
  console.log('parseRegulation input document:', document);
  const sections: Record<string, string> = {};
  
  // セクションごとに分割（新仕様: 8セクション対応）
  const sectionPatterns = [
    { id: 'purpose', pattern: /##?\s*1-1\s*規程の目的[^]*?(?=##|$)/i },
    { id: 'revision_policy', pattern: /##?\s*1-2\s*運用・改訂の方針[^]*?(?=##|$)/i },
    { id: 'important_business', pattern: /##?\s*2-1\s*BCP目標[^]*?(?=##|$)/i },
    { id: 'recovery_strategy', pattern: /##?\s*2-2\s*BCP目標の達成戦略[^]*?(?=##|$)/i },
    { id: 'emergency_headquarters', pattern: /##?\s*2-3\s*緊急対策本部の設置[^]*?(?=##|$)/i },
    { id: 'risk_minimization', pattern: /##?\s*3-1\s*リスクの最小化[^]*?(?=##|$)/i },
    { id: 'bcm_promotion', pattern: /##?\s*3-2\s*BCPの定着[^]*?(?=##|$)/i },
    { id: 'employee_education', pattern: /##?\s*3-3\s*従業員教育[^]*?(?=##|$)/i },
  ];
  
  sectionPatterns.forEach(({ id, pattern }) => {
    const match = document.match(pattern);
    if (match) {
      // セクションタイトルを除去してコンテンツのみ取得
      let content = match[0];
      content = content.replace(/##?\s*[0-9-]*\s*[^\n]+\n/, '').trim();
      sections[id] = content;
      console.log(`Parsed section ${id}:`, content.substring(0, 100) + '...');
    } else {
      console.log(`No match found for section ${id}`);
    }
  });
  
  console.log('parseRegulation output sections:', sections);
  return sections;
};

// 非同期ジョブAPI
export const generateRegulationAsync = async (
  request: RegulationGenerationRequest
): Promise<{ job_id: string }> => {
  try {
    const response = await apiClient.postRaw<{ job_id: string }>(
      '/api/regulation/generate-async',
      request,
      {
        timeout: 10000, // 10秒のタイムアウト(即座に返却されるため)
      }
    );

    console.log('generateRegulationAsync response:', response.data);
    console.log('job_id:', response.data.job_id);

    if (!response.data.job_id) {
      console.error('job_id is missing in response:', response.data);
      throw new Error('job_idがレスポンスに含まれていません');
    }

    return response.data;
  } catch (error: any) {
    console.error('非同期ジョブ作成エラー:', error);
    throw new Error(
      error.response?.data?.detail ||
      '非同期ジョブの作成中にエラーが発生しました'
    );
  }
};

export const getRegulationJobStatus = async (
  jobId: string,
  tenantId?: string
): Promise<RegulationJobResponse> => {
  try {
    const params = new URLSearchParams();
    if (tenantId) {
      params.append('tenant_id', tenantId);
    }

    const url = `/api/regulation/jobs/${jobId}${
      params.toString() ? `?${params.toString()}` : ''
    }`;

    const response = await apiClient.getRaw<RegulationJobResponse>(
      url,
      {
        timeout: 60000, // 60秒のタイムアウト
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('ジョブステータス取得エラー:', error);
    throw new Error(
      error.response?.data?.detail ||
      'ジョブステータスの取得中にエラーが発生しました'
    );
  }
};
